import React, { useState, useEffect } from "react";
import {
  Users,
  Package,
  Heart,
  TrendingUp,
  MessageSquare,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  DollarSign,
  Gift,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  MoreVertical,
} from "lucide-react";
import "../../assets/css/ngodashboard/Ngodashboard.css";
import NGOMasterLayout from "../../components/layout/NGOMasterLayout";

const Ngodashboard = () => {
  const [activeTimeframe, setActiveTimeframe] = useState("week");
  const [animateStats, setAnimateStats] = useState(false);

  useEffect(() => {
    setAnimateStats(true);
  }, []);

  // Stats Data
  const stats = [
    {
      id: 1,
      title: "Total Donors",
      value: "1,248",
      change: "+12.5%",
      isPositive: true,
      icon: Users,
      color: "teal",
      bgGradient: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
    },
    {
      id: 2,
      title: "Active Donations",
      value: "356",
      change: "+8.2%",
      isPositive: true,
      icon: Package,
      color: "orange",
      bgGradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    },
    {
      id: 3,
      title: "Total Impact",
      value: "5,432",
      change: "+15.3%",
      isPositive: true,
      icon: Heart,
      color: "pink",
      bgGradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
    },
    {
      id: 4,
      title: "Monthly Revenue",
      value: "PKR 2.5M",
      change: "-3.1%",
      isPositive: false,
      icon: DollarSign,
      color: "green",
      bgGradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    },
  ];

  // Recent Donations
  const recentDonations = [
    {
      id: 1,
      donor: "Ahmed Khan",
      type: "Food",
      amount: "50kg Rice",
      status: "Completed",
      time: "2 hours ago",
      statusColor: "green",
    },
    {
      id: 2,
      donor: "Fatima Ali",
      type: "Medicine",
      amount: "First Aid Kit",
      status: "Pending",
      time: "4 hours ago",
      statusColor: "orange",
    },
    {
      id: 3,
      donor: "Hassan Ahmed",
      type: "Blood",
      amount: "O+ Blood",
      status: "In Progress",
      time: "5 hours ago",
      statusColor: "blue",
    },
    {
      id: 4,
      donor: "Ayesha Malik",
      type: "Food",
      amount: "30kg Wheat",
      status: "Completed",
      time: "1 day ago",
      statusColor: "green",
    },
  ];

  // Upcoming Events
  const upcomingEvents = [
    {
      id: 1,
      title: "Food Distribution Drive",
      date: "Jan 25, 2026",
      time: "10:00 AM",
      location: "Community Center",
      participants: 45,
    },
    {
      id: 2,
      title: "Medical Camp",
      date: "Jan 28, 2026",
      time: "9:00 AM",
      location: "City Hospital",
      participants: 32,
    },
    {
      id: 3,
      title: "Blood Donation Camp",
      date: "Feb 1, 2026",
      time: "11:00 AM",
      location: "Main Square",
      participants: 28,
    },
  ];

  // Quick Actions
  const quickActions = [
    { id: 1, label: "New Donation", icon: Gift, color: "teal" },
    { id: 2, label: "Add Donor", icon: Users, color: "orange" },
    { id: 3, label: "Send Message", icon: MessageSquare, color: "blue" },
    { id: 4, label: "Generate Report", icon: BarChart3, color: "purple" },
  ];

  // Donation Distribution Data
  const donationTypes = [
    { type: "Food", percentage: 45, color: "#0d9488" },
    { type: "Medicine", percentage: 30, color: "#f97316" },
    { type: "Blood", percentage: 15, color: "#ec4899" },
    { type: "Others", percentage: 10, color: "#10b981" },
  ];

  return (
    <div className="ngo-dashboard-container">
      <NGOMasterLayout>
        {/* Header */}
        <div className="ngo-dashboard-header">
          <div>
            <h1 className="ngo-dashboard-title">Dashboard</h1>
            <p className="ngo-dashboard-subtitle">
              Welcome back! Here's what's happening today.
            </p>
          </div>
          <div className="timeframe-selector">
            {["Today", "Week", "Month", "Year"].map((time) => (
              <button
                key={time}
                className={`timeframe-btn ${activeTimeframe === time.toLowerCase() ? "active" : ""}`}
                onClick={() => setActiveTimeframe(time.toLowerCase())}
              >
                {time}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.id}
                className={`ngo-stat-card ${animateStats ? "animate" : ""}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-header">
                  <div
                    className="stat-icon-wrapper"
                    style={{ background: stat.bgGradient }}
                  >
                    <Icon className="stat-icon" />
                  </div>
                  <button className="stat-menu-btn">
                    <MoreVertical size={18} />
                  </button>
                </div>
                <div className="stat-content">
                  <h3 className="stat-title">{stat.title}</h3>
                  <p className="stat-value">{stat.value}</p>
                  <div
                    className={`stat-change ${stat.isPositive ? "positive" : "negative"}`}
                  >
                    {stat.isPositive ? (
                      <ArrowUpRight size={16} />
                    ) : (
                      <ArrowDownRight size={16} />
                    )}
                    <span>{stat.change}</span>
                    <span className="stat-period">vs last month</span>
                  </div>
                </div>
                <div className="stat-sparkline">
                  <div
                    className="sparkline-bar"
                    style={{ height: "40%" }}
                  ></div>
                  <div
                    className="sparkline-bar"
                    style={{ height: "60%" }}
                  ></div>
                  <div
                    className="sparkline-bar"
                    style={{ height: "80%" }}
                  ></div>
                  <div
                    className="sparkline-bar"
                    style={{ height: "50%" }}
                  ></div>
                  <div
                    className="sparkline-bar"
                    style={{ height: "90%" }}
                  ></div>
                  <div
                    className="sparkline-bar"
                    style={{ height: "70%" }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="content-grid">
          {/* Recent Donations */}
          <div className="ngo-content-card ngo-donations-card">
            <div className="ngo-card-header">
                <h2 className="ngo-card-title">Recent Donations</h2>
                <p className="ngo-card-subtitle">Latest donation activities</p>
              <button className="ngo-view-all-btn">View All</button>
            </div>
            <div className="ngo-donations-list">
              {recentDonations.map((donation, index) => (
                <div
                  key={donation.id}
                  className="donation-item"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="donation-avatar">
                    {donation.donor
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div className="donation-info">
                    <h4 className="donor-name">{donation.donor}</h4>
                    <p className="donation-details">
                      {donation.type} - {donation.amount}
                    </p>
                  </div>
                  <div className="donation-status-wrapper">
                    <span
                      className={`donation-status status-${donation.statusColor}`}
                    >
                      {donation.status}
                    </span>
                    <span className="donation-time">{donation.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Donation Distribution */}
          <div className="ngo-content-card distribution-card">
            <div className="ngo-card-header">
              <div>
                <h2 className="ngo-card-title">Donation Distribution</h2>
                <p className="ngo-card-subtitle">By category</p>
              </div>
              <PieChart className="ngo-card-header-icon" />
            </div>
            <div className="distribution-chart">
              <div className="pie-chart">
                {donationTypes.map((type, index) => (
                  <div
                    key={index}
                    className="pie-segment"
                    style={{
                      "--percentage": type.percentage,
                      "--color": type.color,
                      "--rotation": donationTypes
                        .slice(0, index)
                        .reduce((acc, t) => acc + t.percentage * 3.6, 0),
                    }}
                  ></div>
                ))}
                <div className="pie-center">
                  <div className="pie-total">100%</div>
                  <div className="pie-label">Total</div>
                </div>
              </div>
              <div className="distribution-legend">
                {donationTypes.map((type, index) => (
                  <div key={index} className="legend-item">
                    <div
                      className="legend-color"
                      style={{ background: type.color }}
                    ></div>
                    <span className="legend-label">{type.type}</span>
                    <span className="legend-value">{type.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="ngo-content-card events-card">
            <div className="ngo-card-header">
              <div>
                <h2 className="ngo-card-title">Upcoming Events</h2>
                <p className="ngo-card-subtitle">Scheduled activities</p>
              </div>
              <Calendar className="ngo-card-header-icon" />
            </div>
            <div className="events-list">
              {upcomingEvents.map((event, index) => (
                <div
                  key={event.id}
                  className="event-item"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="event-date">
                    <div className="event-day">
                      {event.date.split(" ")[1].replace(",", "")}
                    </div>
                    <div className="event-month">
                      {event.date.split(" ")[0]}
                    </div>
                  </div>
                  <div className="event-details">
                    <h4 className="event-title">{event.title}</h4>
                    <div className="event-meta">
                      <span className="event-time">
                        <Clock size={14} />
                        {event.time}
                      </span>
                      <span className="event-location">{event.location}</span>
                    </div>
                  </div>
                  <div className="event-participants">
                    <Users size={16} />
                    <span>{event.participants}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="ngo-content-card actions-card">
            <div className="ngo-card-header">
              <div>
                <h2 className="ngo-card-title">Quick Actions</h2>
                <p className="ngo-card-subtitle">Common tasks</p>
              </div>
              <Activity className="ngo-card-header-icon" />
            </div>
            <div className="ngo-quick-actions-grid">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    className={`ngo-action-btn action-${action.color}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="action-icon-wrapper">
                      <Icon size={24} />
                    </div>
                    <span className="action-label">{action.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Activity Summary */}
            <div className="activity-summary">
              <div className="activity-item">
                <div className="activity-icon success">
                  <CheckCircle size={20} />
                </div>
                <div className="activity-info">
                  <span className="activity-count">234</span>
                  <span className="activity-label">Completed</span>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon warning">
                  <AlertCircle size={20} />
                </div>
                <div className="activity-info">
                  <span className="activity-count">45</span>
                  <span className="activity-label">Pending</span>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon danger">
                  <XCircle size={20} />
                </div>
                <div className="activity-info">
                  <span className="activity-count">12</span>
                  <span className="activity-label">Cancelled</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </NGOMasterLayout>
    </div>
  );
};

export default Ngodashboard;
